import { useEffect, useRef, useState } from 'react';
import type { ApiClient } from '../../modules/api';
import { Backdrop, ChevronLeft } from '../../modules/design-system';
import { JustPulledCard } from './JustPulledCard';
import { useJustPulledPaginated } from './useJustPulledPaginated';

/**
 * Same scroll-state hook the PackPage Header uses — kept inline (vs.
 * shared) because it's three lines and importing across modules just
 * to dedupe would obscure the pattern.
 */
function useIsScrolled(threshold = 8): boolean {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const update = () => setScrolled(window.scrollY > threshold);
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, [threshold]);
  return scrolled;
}

export interface JustPulledPageProps {
  client: ApiClient;
  onBack: () => void;
  /** Optional: tapping "Rip Repackz" on a card switches to that offer's pack page. */
  onRip?: (offerId: number) => void;
  /** Pack scope. When set, the feed is limited to pulls from this
   *  pool_offer (matches PackPage's current `gameId`). Omit for the
   *  global all-packs feed. */
  packId?: string | null;
  /** Optional bg image for the pack scope — same hero image used on
   *  the PackPage backdrop. Painted as a viewport-fixed layer that
   *  fades into the gradient. Omit → only the gradient is shown. */
  bgImageUrl?: string | null;
}

/**
 * Full list of recent pulls. Opened either from the global "Just Pulled"
 * strip's chevron (no `packId`) or from a specific pack — in which case
 * the feed is filtered to pulls from that pack and the page reuses the
 * pack's bg image hero so it visually matches the source pack.
 *
 * Layout — Figma 226:363 (desktop) / 226:478 (mobile):
 *   • sticky transparent header — glass back button + centred title
 *   • single column of glassy `JustPulledCard`s (max 600px desktop)
 *   • viewport-fixed gradient + optional bg image backdrop
 */
export function JustPulledPage({
  client,
  onBack,
  onRip,
  packId = null,
  bgImageUrl = null,
}: JustPulledPageProps) {
  const { items, loading, loadingMore, reachedEnd, loadMore } =
    useJustPulledPaginated(client, { poolOfferId: packId });
  const scrolled = useIsScrolled(8);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      { rootMargin: '300px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [loadMore]);

  return (
    // `data-page` wraps Backdrop too so the page-scoped CSS-var
    // redefinition in index.css reaches the gradient layer. Backdrop
    // sat as a sibling before, which left it reading root-level
    // `--ui-bg-gradient` and ignoring page-specific knobs.
    <div data-page="just-pulled">
      {/* Page-wide gradient + optional bg photo pinned to the viewport.
          Painting it on the outer container instead would re-anchor the
          radial centre every time the document grows (infinite-scroll
          pagination), causing the visible "blooms" as new pages append.
          Backdrop's fixed layers keep the gradient locked to the viewport
          regardless of scroll position or content height. */}
      <Backdrop bgImageUrl={bgImageUrl} />
      <div
        data-co-component="JustPulledPageBackground"
        className="@container relative min-h-screen w-full"
        style={{ color: 'var(--color-text-on-brand)' }}
      >
      <header
        data-co-component="JustPulledHeader"
        data-scrolled={scrolled || undefined}
        className={
          'sticky top-0 z-40 ' +
          'grid grid-cols-[44px_minmax(0,1fr)_44px] items-center ' +
          // Hand-rolled `header-safe-top` (index.css): this bar carries its own
          // padding instead of a content container, so the inset has to be
          // folded into `padding-top` rather than added next to it. The
          // 1.25rem is `py-5`, kept on `padding-bottom` as-is.
          'mt-[calc(var(--page-safe-top)*-1)] ' +
          'px-4 pb-5 pt-[calc(1.25rem+var(--page-safe-top))] @[600px]:px-6 ' +
          'transition-[background-color,backdrop-filter,border-color] duration-200 ease-out ' +
          // Same scroll-darken treatment as the PackPage / MyPacks headers.
          // The blur half lives in the inline style below — an inline
          // `backdropFilter` would override a `backdrop-blur-md` class.
          (scrolled
            ? 'bg-black/40 border-b border-white/10'
            : 'border-b border-transparent')
        }
        // Design Constructor surface: `JustPulledHeader`. Operator drives
        // background, blur and the scroll-state hair-line via knobs. The
        // glassBlur FALLBACK is scroll-aware (12px = Tailwind's blur-md,
        // matching the other headers); an operator-set knob wins on both
        // states.
        style={{
          backgroundImage:
            'linear-gradient(' +
            'var(--co-JustPulledHeader-bgDirection, to bottom), ' +
            'color-mix(in srgb, var(--co-JustPulledHeader-bgFrom, rgba(0,0,0,0)) ' +
            'calc(var(--co-JustPulledHeader-bgOpacity, 1) * 100%), transparent) 0%, ' +
            'color-mix(in srgb, var(--co-JustPulledHeader-bgTo, rgba(0,0,0,0)) ' +
            'calc(var(--co-JustPulledHeader-bgOpacity, 1) * 100%), transparent) 100%)',
          backdropFilter: `blur(var(--co-JustPulledHeader-glassBlur, ${scrolled ? '12px' : '0px'}))`,
          WebkitBackdropFilter: `blur(var(--co-JustPulledHeader-glassBlur, ${scrolled ? '12px' : '0px'}))`,
        }}
      >
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          className="
            flex size-10 items-center justify-center rounded-full
            transition-transform active:scale-95
            shadow-[0_64px_32px_0_rgba(0,0,0,0.05)]
          "
          style={{
            background: 'var(--co-JustPulledHeader-backButtonBg, rgba(255,255,255,0.20))',
            borderStyle: 'solid',
            borderWidth: 'var(--co-JustPulledHeader-backButtonBorderWidth, 1px)',
            borderColor: 'var(--co-JustPulledHeader-backButtonBorder, rgba(171,174,179,0.24))',
            color: 'var(--co-JustPulledHeader-backIconColor, #ffffff)',
            backdropFilter: 'blur(27px)',
            WebkitBackdropFilter: 'blur(27px)',
          }}
        >
          <ChevronLeft size={22} />
        </button>
        <h1
          className="text-center font-bold leading-[26px]"
          style={{
            color: 'var(--co-JustPulledHeader-titleColor, #ffffff)',
            fontFamily: 'var(--co-JustPulledHeader-titleFont), Inter, system-ui, sans-serif',
            fontSize: 'var(--co-JustPulledHeader-titleSize, 18px)',
          }}
        >
          Just Pulled
        </h1>
        <span aria-hidden />
      </header>

      <main className="px-3 pb-12 @[600px]:px-6">
        {loading && items.length === 0 ? (
          <SkeletonList />
        ) : items.length === 0 ? (
          <EmptyState />
        ) : (
          <ul className="mx-auto flex max-w-[600px] flex-col gap-2">
            {items.map((item) => (
              <li key={item.id}>
                <JustPulledCard item={item} {...(onRip ? { onRip } : {})} />
              </li>
            ))}
          </ul>
        )}

        {loadingMore && (
          <p className="type-subhead mt-4 text-center text-[color:var(--color-text-subtext)]">
            Loading more…
          </p>
        )}
        {!reachedEnd && <div ref={sentinelRef} className="h-px w-full" aria-hidden />}
      </main>
      </div>
    </div>
  );
}

function SkeletonList() {
  return (
    <ul className="mx-auto flex max-w-[600px] flex-col gap-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <li
          key={i}
          aria-hidden
          className="
            relative w-full overflow-hidden rounded-[28px]
            border-y border-[rgba(171,174,179,0.24)]
            backdrop-blur-[27px]
            shadow-[0_9px_30px_0_rgba(7,10,18,0.08)]
          "
          style={{
            backgroundImage:
              'linear-gradient(180deg, rgba(255,255,255,0.2) 0%, rgba(153,153,153,0.2) 100%), ' +
              'linear-gradient(90deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.18) 100%)',
          }}
        >
          <div
            className="m-2 aspect-square animate-pulse rounded-[25px] bg-white/40"
          />
          <div className="px-4 pb-4 pt-2">
            <div className="h-5 w-3/4 animate-pulse rounded bg-white/30" />
            <div className="mt-2 h-4 w-1/3 animate-pulse rounded bg-white/20" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function EmptyState() {
  return (
    <p className="type-body py-12 text-center text-white/70">
      No recent pulls yet.
    </p>
  );
}
